import { LineItem } from '~api/som';

import { Button, ListLayout, Modal, PileBadge, formatId, styled } from '@ingrid/luterkort-react';

import { Currency } from '~src/components/Currency/Currency';
import { Dimensions } from '~src/components/Dimensions/Dimensions';
import { Weight } from '~src/components/Weight/Weight';
import { useTranslation } from '~src/i18n/use-translation';

interface Props {
  item: LineItem;
}

const Row = styled(ListLayout.Row, {
  '[data-action]': {
    visibility: 'hidden',
  },

  '&:hover [data-action]': {
    visibility: 'visible',
  },
});

export function ConsignmentItem(props: Props) {
  const { item } = props;
  const { t } = useTranslation();

  return (
    <Modal.Root>
      <Row>
        <ListLayout.Stack>
          <ListLayout.SubRow>
            <ListLayout.PrimaryText>
              {item.quantity}x {item.name}
            </ListLayout.PrimaryText>
          </ListLayout.SubRow>

          <ListLayout.SubRow gap="lg">
            <ListLayout.SecondaryText>{formatId(item.sku)}</ListLayout.SecondaryText>
            {item.weight && (
              <ListLayout.SecondaryText>
                <Weight weight={item.weight} />
              </ListLayout.SecondaryText>
            )}
            {item.dimensions && (
              <ListLayout.SecondaryText>
                <Dimensions dimensions={item.dimensions} />
              </ListLayout.SecondaryText>
            )}
          </ListLayout.SubRow>
        </ListLayout.Stack>

        <ListLayout.Actions data-action>
          <Modal.Trigger>
            <Button variant="tertiary">{t('common.details')}</Button>
          </Modal.Trigger>
        </ListLayout.Actions>
      </Row>

      <Modal.Content>
        <Modal.Header title={t('shipment.details.lineItemDetail.title')} />

        <Modal.Body>
          <ListLayout.Stack>
            <ListLayout.Text variant="secondary">{t('common.name')}</ListLayout.Text>
            <ListLayout.Text variant="primary">{item.name ?? '-'}</ListLayout.Text>
          </ListLayout.Stack>

          <ListLayout.Row>
            <ListLayout.Stack style={{ flex: 1 }}>
              <ListLayout.Text variant="secondary">{t('common.quantity')}</ListLayout.Text>
              <ListLayout.Text variant="primary">{item.quantity ?? '-'}</ListLayout.Text>
            </ListLayout.Stack>

            <ListLayout.Stack style={{ flex: 1 }}>
              <ListLayout.Text variant="secondary">{t('common.price')}</ListLayout.Text>
              <ListLayout.Text variant="primary">
                {item.price && item.currency ? <Currency value={item.price} currency={item.currency} /> : '-'}
              </ListLayout.Text>
            </ListLayout.Stack>
          </ListLayout.Row>

          <ListLayout.Row>
            <ListLayout.Stack style={{ flex: 1 }}>
              <ListLayout.Text variant="secondary">SKU</ListLayout.Text>
              <ListLayout.Text variant="primary">{item.sku ?? '-'}</ListLayout.Text>
            </ListLayout.Stack>

            <ListLayout.Stack style={{ flex: 1 }}>
              <ListLayout.Text variant="secondary">{t('common.hsTariffNumber')}</ListLayout.Text>
              <ListLayout.Text variant="primary">{item.hs_tariff_number ?? '-'}</ListLayout.Text>
            </ListLayout.Stack>
          </ListLayout.Row>

          <ListLayout.Row>
            <ListLayout.Stack style={{ flex: 1 }}>
              <ListLayout.Text variant="secondary">{t('common.weight')}</ListLayout.Text>
              <ListLayout.Text variant="primary">{item.weight ? <Weight weight={item.weight} /> : '-'}</ListLayout.Text>
            </ListLayout.Stack>

            <ListLayout.Stack style={{ flex: 1 }}>
              <ListLayout.Text variant="secondary">{t('common.dimensions')}</ListLayout.Text>
              <ListLayout.Text variant="primary">
                {item.dimensions ? <Dimensions dimensions={item.dimensions} /> : '-'}
              </ListLayout.Text>
            </ListLayout.Stack>
          </ListLayout.Row>

          <ListLayout.Stack>
            <ListLayout.Text variant="secondary">{t('common.description')}</ListLayout.Text>
            <ListLayout.Text variant="primary">{item.description ?? '-'}</ListLayout.Text>
          </ListLayout.Stack>

          <ListLayout.Stack>
            <ListLayout.Text variant="secondary">{t('common.tags')}</ListLayout.Text>
            {item.tags?.length ? (
              <ListLayout.Row style={{ gap: '8px' }}>
                {item.tags?.map((tag, index) => (
                  <PileBadge variant="neutral" key={`tag-${index}`}>
                    {tag}
                  </PileBadge>
                ))}
              </ListLayout.Row>
            ) : (
              <ListLayout.Text variant="primary">-</ListLayout.Text>
            )}
          </ListLayout.Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
